import { z } from 'zod';
/**
 * The organisation, as the ledger declares it (ADR-0036, 0037, 0038).
 *
 * Three files that have to agree: who the people are, what the departments
 * are, and how the shared drive is arranged. They agree because they are
 * validated together — a roster naming a department that does not exist, or
 * a folder owned by one, fails here rather than at provisioning time.
 *
 * These are **declarations, not state**. What is actually provisioned lives
 * in `platform.human` and `platform.agent_identity`; this is what should be,
 * and the difference between the two is what a provisioning run reports.
 */
const email = z.string().email();
const slug = z.string().regex(/^[a-z][a-z_0-9]*$/);
// ---------------------------------------------------------------------------
// People and their twins
// ---------------------------------------------------------------------------
export const twinSchema = z
    .object({
    /**
     * The twin's own name (ADR-0036).
     *
     * Its own, and never derived from its principal's: "Iryna Agent" beside
     * Iryna in a Chat roster is the confusion §16 forbids, arrived at by
     * following two rules that are each individually right.
     */
    givenName: z.string().regex(/^[A-Z][a-z]+$/),
    /**
     * And no address (ADR-0061). A twin has no Workspace account: it works
     * inside its principal's own account and is reached in the internal
     * chat, rendered "Sol · Iryna's twin" (§16 as amended by 0004,
     * ADR-0052). `.strict()` is what refuses an `email:` key here, so a
     * roster that gives a twin an address fails to load rather than
     * quietly provisioning one.
     */
    purpose: z.string().min(1).max(500),
})
    .strict();
export const personSchema = z
    .object({
    email,
    displayName: z.string().min(1).max(120),
    superadmin: z.boolean().default(false),
    function: z.string().min(1).max(500),
    /** Departments this person is accountable for day to day. */
    leads: z.array(slug).default([]),
    /** Departments whose agents and approvals they sponsor (§3). */
    sponsors: z.array(slug).default([]),
    /**
     * Platform roles the constitution names (§9, §12): the CTO and the CPO
     * decide what is promoted into everyone's baseline. Granted as
     * `platform.cto` / `platform.cpo` — a role, never a department, so it
     * confers no membership of Platform and no reach for their twin.
     */
    roles: z.array(z.enum(['cto', 'cpo'])).default([]),
    twin: twinSchema.optional(),
})
    .strict();
export const rosterSchema = z
    .object({
    version: z.number().int().positive(),
    humans: z.array(personSchema).min(1),
    twinDefaults: z
        .object({
        // §7 and a database CHECK. Declared so the roster states it rather
        // than a reader having to know it. No org unit: a twin has no
        // directory entry to put in one (ADR-0052).
        familyName: z.literal('Agent'),
        agentKind: z.literal('twin'),
        /**
         * What every twin is assigned on creation (§15, ADR-0052, ADR-0055).
         *
         * Declared once here rather than per person, because it is the same
         * for every twin by design: the tools it may reach, and one
         * capability per department its principal leads or sponsors —
         * `scopesPerDepartment: ['infer']` becomes `outbound.infer`,
         * `finance.infer`, … for that person. §16 then intersects the lot
         * with the principal's own scopes on every call, so a twin never
         * reaches a department its person does not.
         *
         * Optional in the schema so a roster without twins that act stays
         * valid; present in the file, because a twin with no assignment is
         * a chat that never answers.
         */
        assignment: z
            .object({
            title: z.string().min(1).max(120),
            tools: z.array(z.string().regex(/^[a-z][a-z_0-9]*\.[a-z][a-z_0-9]*$/)).min(1),
            scopesPerDepartment: z.array(z.string().regex(/^[a-z][a-z_0-9]*$/)).min(1),
        })
            .strict()
            .optional(),
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------
export const departmentSchema = z
    .object({
    name: slug,
    displayName: z.string().min(1).max(120),
    purpose: z.string().min(1).max(1000),
    lead: email,
    sponsor: email,
    status: z.enum(['pilot', 'planned', 'live', 'retired']),
    /**
     * Whether this department's agents may write anything at all (§6,
     * ADR-0037).
     *
     * A closed enum rather than a boolean, because the YAML then says what it
     * means at the point a person reads it, and a third mode later is a new
     * member rather than a second flag that has to agree with the first.
     *
     * `read_only` is enforced, not documented: `platform.department.read_only`
     * carries it to the database, where a trigger refuses any scope grant
     * whose capability is not `read`, and `decide()` refuses such a call at
     * the gateway. Finance is the department this exists for — it reports on
     * the others, so nothing it is compromised into doing should be able to
     * change them.
     */
    access: z.enum(['read_write', 'read_only']).default('read_write'),
    specRef: z.string().optional(),
})
    .strict();
export const departmentIndexSchema = z
    .object({
    version: z.number().int().positive(),
    departments: z.array(departmentSchema).min(1),
})
    .strict();
// ---------------------------------------------------------------------------
// The shared drive
// ---------------------------------------------------------------------------
/**
 * A folder key: stable, and never the folder's name (ADR-0038).
 *
 * `02 Outbound` may be renamed to `02 Sales` and every reference still
 * resolves, because nothing references the name. Dotted for nesting, so a
 * key reads as a path without being one.
 */
const folderKey = z.string().regex(/^[a-z][a-z_0-9]*(\.[a-z][a-z_0-9]*)*$/);
export const retentionSchema = z.union([
    z.literal('permanent'),
    z.string().regex(/^\d+[ym]$/),
]);
/** Who may put a file here. Narrower than read access, on purpose. */
export const writerSchema = z.enum(['human', 'agent']);
/**
 * Whose documents a folder holds, by what they are to us (ADR-0063's
 * relationship), so a company's record can say where its papers go without
 * code guessing it. Stated in the taxonomy because the taxonomy is where a
 * person decides where things live (ADR-0038).
 */
export const folderHoldsSchema = z.array(z.enum(['client', 'vendor', 'partner', 'investor'])).min(1);
const identity = {
    key: folderKey,
    name: z.string().min(1).max(120),
    purpose: z.string().min(1).max(600),
    holds: folderHoldsSchema.optional(),
    /**
     * One sub-folder per company, named for it. The sub-folder's identity is
     * this key and the company's id; its name is presentation (ADR-0038).
     */
    onePer: z.literal('company').optional(),
};
/**
 * A child states only what differs from its parent.
 *
 * Optional rather than defaulted, and the distinction is load-bearing: a
 * default cannot be told apart from a value someone chose, so a child that
 * said nothing about its department would silently become the company's.
 * That is not a cosmetic difference — it means no department grant reaches
 * the folder, and an Outbound agent that can read `02 Outbound` cannot read
 * `02 Outbound/Proposals`. A permissions bug that surfaces months later as
 * "the agent can't find the file".
 */
export const childFolderSchema = z
    .object({
    ...identity,
    department: slug.nullable().optional(),
    writableBy: z.array(writerSchema).min(1).optional(),
    retention: retentionSchema.optional(),
})
    .strict();
export const folderSchema = z
    .object({
    ...identity,
    /** The department §6 isolation attaches to. `null` means the company. */
    department: slug.nullable().default(null),
    writableBy: z.array(writerSchema).min(1).default(['human']),
    retention: retentionSchema.default('permanent'),
    children: z.array(childFolderSchema).default([]),
})
    .strict();
export const driveTaxonomySchema = z
    .object({
    version: z.number().int().positive(),
    drive: z
        .object({
        name: z.string().min(1).max(120),
        kind: z.literal('shared_drive'),
        /** Where `apps/ledger-sync` writes. A key, not a path. */
        ledgerMirror: folderKey,
    })
        .strict(),
    folders: z.array(folderSchema).min(1),
})
    .strict();
/**
 * When a file in this folder stops being one the company keeps.
 *
 * **Nothing here deletes, and nothing in this platform can.** ADR-0068
 * refuses any manifest naming a tool Google annotates
 * `destructiveHint: true`, which `drive.files.delete` is — so no agent
 * holds a deleter and none can be granted one without that decision being
 * reversed. Rule 9's "a retention policy a human set may still destroy on
 * a clock" is about the person, not the platform.
 *
 * What this exists for is the other half: computing the date a policy
 * names, so the overdue can be *reported* to whoever owns the folder.
 * `permanent` returns null, which is not "no answer" — it is the answer.
 */
export function retentionCutoff(retention, now) {
    if (retention === 'permanent')
        return null;
    const match = /^(\d+)([ym])$/.exec(retention);
    if (!match) {
        // The schema admits only `permanent` or `<n>y`/`<n>m`, so reaching
        // here means the schema and this function disagree — and guessing a
        // date would silently shorten somebody's retention.
        throw new Error(`"${retention}" is not a retention this platform can date. The taxonomy admits ` +
            '"permanent", "<n>y" or "<n>m"; a value outside that would have to be guessed at, ' +
            'and a guessed cutoff destroys records early.');
    }
    const amount = Number(match[1]);
    const cutoff = new Date(now.getTime());
    if (match[2] === 'y')
        cutoff.setUTCFullYear(cutoff.getUTCFullYear() - amount);
    else
        cutoff.setUTCMonth(cutoff.getUTCMonth() - amount);
    return cutoff;
}
/**
 * Every folder in the tree, flattened, with inheritance resolved.
 *
 * Parents before children, so a caller creating folders never looks for a
 * parent id it has not resolved yet.
 */
export function flattenFolders(taxonomy) {
    const out = [];
    for (const folder of taxonomy.folders) {
        out.push({
            key: folder.key,
            name: folder.name,
            path: folder.name,
            purpose: folder.purpose,
            department: folder.department,
            writableBy: folder.writableBy,
            retention: folder.retention,
            holds: folder.holds ?? [],
            onePer: folder.onePer ?? null,
        });
        for (const child of folder.children) {
            out.push({
                key: child.key,
                name: child.name,
                path: `${folder.name}/${child.name}`,
                purpose: child.purpose,
                // A child belongs to its parent's department unless it says
                // otherwise. Narrowing writers or shortening retention is a
                // deliberate override; silently widening the owner is not.
                department: child.department === undefined ? folder.department : child.department,
                writableBy: child.writableBy ?? folder.writableBy,
                retention: child.retention ?? folder.retention,
                // Not inherited: that a parent holds clients' papers does not make
                // every child a client's folder.
                holds: child.holds ?? [],
                onePer: child.onePer ?? null,
            });
        }
    }
    return out;
}
/**
 * The human scopes that are roles rather than a department's capability
 * (§9, §12). `platform.human_scope` holds both, and every reader that turns
 * a scope into a department — session membership, a twin's ceiling — must
 * skip these, or naming someone CPO would quietly put them in Platform.
 */
export const ROLE_SCOPES = ['platform.cto', 'platform.cpo'];
//# sourceMappingURL=org.js.map